import React, {useCallback, useMemo} from 'react';

import {DataFrameColumnMappingTable} from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnMappingTable';
import isArchived from 'archive/isArchived';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';

import ColumnMappingTable from './column-mapping-table/ColumnMappingTable';
import {
  DataFrameColumnToFieldMapping,
  DataMappingState,
  TargetSpecificMappingState,
} from './DataMappingTypes';
import {
  buildColumnDefMap,
  buildTargetColumnMappings,
  COLUMN_MAPPING_CONFIG_BY_TARGET,
  REQUIRED_COLUMN_DEFS_BY_TARGET,
  resolveSelectedTargetColumns,
  SaveToResultsTarget,
} from './targetSpecificColumns';
import validateSchemaMapping, {validateColumnMapping} from './validateDataMapping';

export interface SaveToResultsStepperProps {
  resultSchema: ResultSchema | null;
  inputDataFrameSpec: {name: string; columns: Array<DataFrameColumnDef>};
  columnMapping: Array<DataFrameColumnToFieldMapping>;
  targetSpecificMapping: TargetSpecificMappingState | null;
  onSchemaChange: (newSchema: ResultSchema | null) => void;
  onMappingsChange: (newMappings: Array<DataFrameColumnToFieldMapping>) => void;
  onTargetSpecificMappingChange: (newState: TargetSpecificMappingState | null) => void;
  disabled?: boolean;
  checkboxClassName?: string;
}

export default function SaveToResultsStepper({
  resultSchema,
  inputDataFrameSpec,
  columnMapping,
  targetSpecificMapping,
  onSchemaChange,
  onMappingsChange,
  onTargetSpecificMappingChange,
  disabled,
  checkboxClassName,
}: SaveToResultsStepperProps): JSX.Element {
  const inputColumns = inputDataFrameSpec.columns;

  const dataMappingState: DataMappingState = useMemo(
    () => ({resultSchema, mappings: columnMapping}),
    [resultSchema, columnMapping]
  );

  const validationErrors = validateSchemaMapping(dataMappingState);

  const hasTargetMapping = targetSpecificMapping?.target != null;

  const targetColumnDefs = useMemo(
    () => (hasTargetMapping ? REQUIRED_COLUMN_DEFS_BY_TARGET[targetSpecificMapping.target] : []),
    [hasTargetMapping, targetSpecificMapping?.target]
  );

  const targetColumnValidationErrors = hasTargetMapping
    ? validateColumnMapping(targetSpecificMapping.columnMappings, targetColumnDefs, inputColumns, {
        outputColumnConfig: COLUMN_MAPPING_CONFIG_BY_TARGET[targetSpecificMapping.target],
      })
    : [];

  const columnDefMap = useMemo(() => buildColumnDefMap(inputColumns), [inputColumns]);

  const selectedTargetColumns = useMemo(
    () => resolveSelectedTargetColumns(targetSpecificMapping, targetColumnDefs, columnDefMap),
    [targetSpecificMapping, targetColumnDefs, columnDefMap]
  );

  const setSelectedColumnsHandler = useCallback(
    (selectedColumnDefs: Array<DataFrameColumnDef | null>) => {
      if (!hasTargetMapping) {
        return;
      }
      onTargetSpecificMappingChange({
        ...targetSpecificMapping,
        columnMappings: buildTargetColumnMappings(selectedColumnDefs, targetColumnDefs),
      });
    },
    [hasTargetMapping, targetSpecificMapping, targetColumnDefs, onTargetSpecificMappingChange]
  );

  return (
    <Stepper>
      <StepperStep title="Select Result schema" id="schema">
        <QueryingResultSchemaSelector
          selectedSchema={resultSchema}
          onChange={onSchemaChange}
          filter={(rs) => !isArchived(rs)}
          requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
          isDisabled={disabled}
        />
      </StepperStep>
      {resultSchema != null && (
        <StepperStep title="Map columns to Results" id="map">
          <ColumnMappingTable
            inputDataFrameSpec={inputDataFrameSpec}
            outputResultSchema={resultSchema}
            mappings={columnMapping}
            setMappings={onMappingsChange}
            errors={validationErrors}
          />
        </StepperStep>
      )}
      {BenchlingConfig.SAMPLE_TESTS_ENABLE && (
        <StepperStep title="Configure downstream target">
          <CheckboxInput
            className={checkboxClassName}
            checked={targetSpecificMapping?.target === SaveToResultsTarget.TEST_ORDER_TABLE}
            onChange={(ev) => {
              if (ev.target.checked) {
                onTargetSpecificMappingChange({
                  target: SaveToResultsTarget.TEST_ORDER_TABLE,
                  targetVersion: 0,
                  columnMappings: [],
                });
              } else {
                onTargetSpecificMappingChange(null);
              }
            }}
            id="target-assay-checkbox"
            label="Use this result in an assay"
            disabled={disabled}
          />
        </StepperStep>
      )}
      {BenchlingConfig.SAMPLE_TESTS_ENABLE && targetSpecificMapping != null && (
        <StepperStep title="Map columns for downstream target">
          <DataFrameColumnMappingTable
            templateDataFrameColumns={REQUIRED_COLUMN_DEFS_BY_TARGET.TEST_ORDER_TABLE}
            newDataFrameColumns={inputColumns}
            setSelectedColumns={setSelectedColumnsHandler}
            selectedColumns={selectedTargetColumns}
            errors={targetColumnValidationErrors}
          />
        </StepperStep>
      )}
    </Stepper>
  );
}
